
import Framework7 from "framework7";
import { Dialog } from "framework7/types";
import party from "party-js";
import { tr } from "./i18n";
import { getRawStoredValue, setRawStoredValue } from "./settings";

export const round = (value: number, decimals: number) => {
    return Number(Math.round(Number(`${value}e${decimals}`)) + 'e-' + decimals);
}

function throwError(errorMessage: string): never {
    throw new Error(errorMessage);
}

export function getErrorMessage(error: unknown): string {
    if (error instanceof Error) return error.message;
    return String(error);
}

export interface RandomStringGenerator {
    getNext(): string;
}

/**
 * @param str The input string
 * @param darkMode Boolean to shift lightness for dark themes
 */
export const stringToColor = (str: string, darkMode: boolean = true): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const h = Math.abs(hash) % 360;

    // Configuration for Dark Mode (Pastels) vs Light Mode (Vibrant)
    const s = darkMode ? 60 : 70; // Lower saturation for dark mode
    const l = darkMode ? 75 : 45; // Higher lightness for dark mode

    return `hsl(${h}, ${s}%, ${l}%)`;
};

/**
 * Returns a new string every time, and avoid showing the ones already shown. 
 * Uses settings to persist data.
 */
export class PersistentRandomStringGenerator implements RandomStringGenerator {
    private readonly name: string;
    private readonly stringList: string[];
    private readonly usedIndices: Set<number>;

    constructor(name: string, stringList: string[]) {
        this.name = name ?? throwError('name cannot be null');
        this.stringList = stringList ?? throwError('stringList cannot be null');
        const storedVals = getRawStoredValue(`prsg-${this.name}`, []); // empty array by default
        this.usedIndices = new Set(storedVals);
    }

    getNext(): string {
        if (this.usedIndices.size === this.stringList.length) {
            this.reset();
        }

        let randomIndex: number;
        do {
            randomIndex = Math.floor(Math.random() * this.stringList.length);
        } while (this.usedIndices.has(randomIndex));

        this.usedIndices.add(randomIndex);
        setRawStoredValue(`prsg-${this.name}`, Array.from(this.usedIndices)); // store as array

        return this.stringList[randomIndex];
    }

    private reset(): void {
        this.usedIndices.clear();
    }
}

interface FireworksOptions {
    type: "confetti" | "sparkles",
    count: number,
    spread: number,
    size: number,
    delay: number
}

/** Show confetti or sparkles on the given element after a delay */
export const showFireworks = (
    partyElem: HTMLElement,
    options: Partial<FireworksOptions> = {}
) => {
    const {
        type = "confetti",
        count = 50,
        spread = 25,
        size = 1,
        delay = 200
    } = options;

    console.debug("Showing fireworks: " + type);
    setTimeout(() => {
        if (type === "confetti") {
            party.confetti(partyElem, { count: count, spread: spread, size: size });
        } else {
            party.sparkles(partyElem, { count: count, size: size });
        }
    }, delay);
}
/**
 * Displays a "Thank You" dialog using the provided Framework7 instance
 *
 * @param f7 - The Framework7 instance used to show the dialog.
 */
export const showThankYouDialog = (f7: Framework7) => {
    const dialog = f7.dialog.alert(tr("common.thank_you"));
    showFireworks(dialog.el, {
        type: "sparkles",
        count: 20,
        size: 1,
        delay: 100,
        spread: 40
    });
};


/**
 * Opens an F7 dialog and waits fora close event.
 * @param {Dialog.Dialog} dialog - the dialog from f7.dialog.create()
 * @returns {Promise<void>} A promise that resolves when the dialog closes
 */
export function openAwaitableDialog(dialog: Dialog.Dialog): Promise<void> {
    return new Promise(resolve => {
        dialog.once('close', () => resolve());
        dialog.open();
    });
}

/**
 * Remove underscores and capitalize every word
 * 
 * @param str 
 * @returns a new string
 */
export function capitalize(str: string): string {
    return str
        .replace(/_/g, ' ')
        // Matches any Unicode letter that follows either the start of the string or a space/punctuation
        .replace(/\p{L}+/gu, (word) => {
            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        });
}


export const roundValue = (val: number | string, digits: number = 1): number => {
    return Number(Number(val).toFixed(digits));
};
